/**
 * Co-parentage — Sac à dos virtuel (port `vCpBag` proto l. 2149-2202).
 *
 * Phase 1, incrément 17. Vue dédiée au sac à dos virtuel — fonctionnalité
 * critique CLAUDE.md §2.3 : « statuts ok/missing, transferts manuels,
 * détection IA d'oublis récurrents. » L'IA est déjà câblée via
 * `aiEngine.detectPatterns` (incr. 11) — chaque render de CpHome
 * incrémente le compteur d'oublis et déclenche `recurring_miss` ≥ 3.
 *
 * Composition (fidèle au proto) :
 *   - Header card (background rouge si missing, vert si complet).
 *   - Banner AI « ⚽ Activité sport demain » si une activité sport est
 *     planifiée today/tomorrow (via `analyzeContext`).
 *   - Card « ⚠️ Manquants » (rendue uniquement si > 0) avec rond
 *     missing cliquable (toggle) + bouton « ✔ Transféré » (swap A/B).
 *   - Card « ✅ Présents » avec rond ok cliquable + badge catégorie.
 *   - Empty state si aucun objet.
 *   - Bouton + Ajouter un objet → modal.
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons, type IconName } from '../../ui/icons.js';
import { aiSuggestion } from '../../ui/AiSuggestion.js';
import styles from './BagView.module.css';
import { t, tn } from '../../i18n/index.js';
import {
  addBagItem,
  getCurrentHome,
  removeBagItem,
  toggleBagItemStatus,
  transferBagItem,
} from '../../stores/homes.js';
import { closeModal, getModal, openModal } from '../../stores/ui.js';
import { modalTemplate } from '../../ui/Modal.js';
import { interpolate } from '../ai-engine/logic.js';
import { analyzeContext } from '../ai-engine/index.js';
import { getCustodyForDate } from './custody.js';
import { editorialHero } from '../../ui/Hero.js';
import type {
  BagCategory,
  BagItem,
  CoparentingHomeData,
  CustodyConfig,
} from '@nestor/shared-types';

const MODAL_ADD = 'cp-bag:add';

const CATEGORY_KEYS: Record<BagCategory, string> = {
  essentiel: 'cp_bag_cat_essentiel',
  sport: 'cp_bag_cat_sport',
  école: 'cp_bag_cat_ecole',
  médical: 'cp_bag_cat_medical',
  autre: 'cp_bag_cat_autre',
};

// Audit D2 : icônes filaires (plus d'emoji UI).
const CATEGORY_ICONS: Record<BagCategory, IconName> = {
  essentiel: 'spark',
  sport: 'ball',
  école: 'book',
  médical: 'health',
  autre: 'box',
};

interface FormState {
  item: string;
  icon: string;
  category: BagCategory;
  location: string;
}

const formState: FormState = {
  item: '',
  icon: '',
  category: 'essentiel',
  location: '',
};

function categoryLabel(cat: BagCategory): string {
  return t(CATEGORY_KEYS[cat] as Parameters<typeof t>[0]);
}

// ── Status header ──────────────────────────────────────────────────

function statusCardTemplate(
  bag: readonly BagItem[],
  custody: CustodyConfig | null,
): TemplateResult {
  const missing = bag.filter((b) => b.status === 'missing');
  const ok = bag.filter((b) => b.status === 'ok');
  const cardClass = missing.length
    ? `${styles.statusCard} ${styles.statusCardMissing}`
    : styles.statusCard;
  const titleClass = missing.length
    ? `${styles.statusTitle} ${styles.statusTitleMissing}`
    : styles.statusTitle;
  const titleText = missing.length
    ? interpolate(tn('cp_bag_status_missing', missing.length), {
        count: missing.length.toString(),
      })
    : t('cp_bag_status_ok');
  const todayParent = getCustodyForDate(new Date(), custody) ?? custody?.parentA ?? '';
  const summary = interpolate(t('cp_bag_summary_template'), {
    ok: ok.length.toString(),
    total: bag.length.toString(),
    parent: todayParent,
  });
  return html`<div class=${cardClass} data-testid="cp-bag-status-card">
    <div class=${styles.statusRow}>
      <div class=${styles.statusEmoji}>${Icon(Icons.bag, 24)}</div>
      <div>
        <div class=${titleClass} data-testid="cp-bag-status-title">${titleText}</div>
        <div class=${styles.statusSummary}>${summary}</div>
      </div>
    </div>
  </div>`;
}

// ── Bag item rows ──────────────────────────────────────────────────

function missingItemRowTemplate(homeId: string, item: BagItem): TemplateResult {
  const onToggle = (): void => {
    toggleBagItemStatus(homeId, item.id);
  };
  const onTransfer = (): void => {
    transferBagItem(homeId, item.id, null);
  };
  const onRemove = (): void => {
    removeBagItem(homeId, item.id);
  };
  return html`<div class=${styles.bagItem} data-testid="cp-bag-row-${item.id}">
    <button
      type="button"
      class=${`${styles.bagStatus} ${styles.missing}`}
      data-testid="cp-bag-toggle-${item.id}"
      aria-label="Toggle status"
      @click=${onToggle}
    >
      !
    </button>
    <span class=${styles.bagIcon}
      >${item.icon
        ? item.icon
        : Icon(Icons[CATEGORY_ICONS[item.category ?? 'autre'] ?? 'box'], 15)}</span
    >
    <div class=${styles.bagInfo}>
      <div class=${styles.bagItemNameMissing}>${item.item}</div>
      <div class=${styles.bagLocation}>${item.location}</div>
    </div>
    <button
      type="button"
      class=${styles.transferBtn}
      data-testid="cp-bag-transfer-${item.id}"
      @click=${onTransfer}
    >
      ${t('cp_bag_btn_transfer')}
    </button>
    <button
      type="button"
      class=${styles.removeBtn}
      data-testid="cp-bag-remove-${item.id}"
      aria-label=${t('cp_bag_remove')}
      @click=${onRemove}
    >
      ×
    </button>
  </div>`;
}

function presentItemRowTemplate(homeId: string, item: BagItem): TemplateResult {
  const onToggle = (): void => {
    toggleBagItemStatus(homeId, item.id);
  };
  const onRemove = (): void => {
    removeBagItem(homeId, item.id);
  };
  return html`<div class=${styles.bagItem} data-testid="cp-bag-row-${item.id}">
    <button
      type="button"
      class=${`${styles.bagStatus} ${styles.ok}`}
      data-testid="cp-bag-toggle-${item.id}"
      aria-label="Toggle status"
      @click=${onToggle}
    >
      ✓
    </button>
    <span class=${styles.bagIcon}
      >${item.icon
        ? item.icon
        : Icon(Icons[CATEGORY_ICONS[item.category ?? 'autre'] ?? 'box'], 15)}</span
    >
    <div class=${styles.bagInfo}>
      <div class=${styles.bagItemName}>${item.item}</div>
      <div class=${styles.bagLocation}>${item.location}</div>
    </div>
    <span class=${styles.bagBadge}>${categoryLabel(item.category)}</span>
    <button
      type="button"
      class=${styles.removeBtn}
      data-testid="cp-bag-remove-${item.id}"
      aria-label=${t('cp_bag_remove')}
      @click=${onRemove}
    >
      ×
    </button>
  </div>`;
}

// ── Modal form ─────────────────────────────────────────────────────

function modalContentTemplate(homeId: string, custody: CustodyConfig | null): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    const item = formState.item.trim();
    if (!item) return;
    addBagItem(homeId, {
      item,
      icon: formState.icon,
      category: formState.category,
      location: formState.location || custody?.parentA || 'Maison',
    });
    formState.item = '';
    formState.icon = '';
    closeModal();
  };
  return html`<form @submit=${onSubmit}>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_bag_label_item')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cp-bag-form-item"
        placeholder=${t('cp_bag_placeholder_item')}
        .value=${formState.item}
        @input=${(e: Event): void => {
          formState.item = (e.target as HTMLInputElement).value;
        }}
        required
      />
    </div>
    <div class=${styles.formGroupRow}>
      <div class=${styles.formGroup}>
        <label class=${styles.formLabel}>${t('cp_bag_label_icon')}</label>
        <input
          class=${styles.formInput}
          type="text"
          data-testid="cp-bag-form-icon"
          placeholder=""
          maxlength="2"
          .value=${formState.icon}
          @input=${(e: Event): void => {
            formState.icon = (e.target as HTMLInputElement).value;
          }}
        />
      </div>
      <div class=${styles.formGroup}>
        <label class=${styles.formLabel}>${t('cp_bag_label_category')}</label>
        <select
          class=${styles.formSelect}
          data-testid="cp-bag-form-category"
          .value=${formState.category}
          @change=${(e: Event): void => {
            formState.category = (e.target as HTMLSelectElement).value as BagCategory;
          }}
        >
          ${(Object.keys(CATEGORY_KEYS) as BagCategory[]).map(
            (cat) => html`<option value=${cat}>${categoryLabel(cat)}</option>`,
          )}
        </select>
      </div>
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_bag_label_location')}</label>
      <select
        class=${styles.formSelect}
        data-testid="cp-bag-form-location"
        .value=${formState.location}
        @change=${(e: Event): void => {
          formState.location = (e.target as HTMLSelectElement).value;
        }}
      >
        <option value=${custody?.parentA ?? 'Parent A'}>
          ${t('cp_bag_loc_at')} ${custody?.parentA ?? 'Parent A'}
        </option>
        <option value=${custody?.parentB ?? 'Parent B'}>
          ${t('cp_bag_loc_at')} ${custody?.parentB ?? 'Parent B'}
        </option>
        <option value=${t('cp_bag_loc_home')}>${t('cp_bag_loc_home')}</option>
      </select>
    </div>
    <button type="submit" class=${styles.formSubmit} data-testid="cp-bag-form-submit">
      ${t('cp_bag_btn_add')}
    </button>
  </form>`;
}

// ── Public render ──────────────────────────────────────────────────

export function renderCpBagView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'coparenting') {
    return html`<section class=${styles.view} data-testid="view-cp-bag">
      <p class=${styles.empty}>${t('cp_home_no_custody')}</p>
    </section>`;
  }
  const data = home.data as CoparentingHomeData;
  const bag = data.bag;
  const missing = bag.filter((b) => b.status === 'missing');
  const present = bag.filter((b) => b.status === 'ok');

  // AI : activité sport today/tomorrow → banner.
  const ctx = analyzeContext(data, new Date());
  const hasSportTmrw = ctx.upcomingActivities.some((a) => a.type === 'sport');

  const onAddClick = (): void => {
    formState.item = '';
    formState.icon = '';
    formState.category = 'essentiel';
    formState.location = data.custody?.parentA ?? 'Parent A';
    openModal(MODAL_ADD);
  };
  const showModal = getModal() === MODAL_ADD;

  return html`
    <section class=${styles.view} data-testid="view-cp-bag">
      ${editorialHero({
        badge: { mode: 'coparenting', label: home.name },
        dateLine: t('cp_bag_dateline'),
        title: t('cp_bag_hero_title'),
        accent: t('cp_bag_hero_accent'),
      })}
      ${statusCardTemplate(bag, data.custody)}
      ${hasSportTmrw
        ? aiSuggestion({
            text: t('cp_bag_ai_sport_tomorrow'),
            kicker: t('cp_home_ai_suggests'),
            testid: 'cp-bag-ai-sport',
          })
        : html``}
      ${missing.length > 0
        ? html`<div class=${styles.card} data-testid="cp-bag-card-missing">
            <div class=${`${styles.cardTitle} ${styles.cardTitleMissing}`}>
              ${t('cp_bag_card_missing_title')}
            </div>
            ${missing.map((b) => missingItemRowTemplate(home.id, b))}
          </div>`
        : html``}
      <div class=${styles.card} data-testid="cp-bag-card-present">
        <div class=${`${styles.cardTitle} ${styles.cardTitlePresent}`}>
          ${t('cp_bag_card_present_title')}
          <span class=${styles.chip}>${present.length.toString()}</span>
        </div>
        ${bag.length === 0
          ? html`<div class=${styles.empty} data-testid="cp-bag-empty">${t('cp_bag_empty')}</div>`
          : present.map((b) => presentItemRowTemplate(home.id, b))}
      </div>
      <button
        type="button"
        class=${styles.addBtn}
        data-testid="cp-bag-add-btn"
        @click=${onAddClick}
      >
        ${t('cp_bag_btn_add')}
      </button>
      ${showModal
        ? modalTemplate({
            title: t('cp_bag_modal_title'),
            content: modalContentTemplate(home.id, data.custody),
            onClose: closeModal,
            testid: 'cp-bag-modal',
          })
        : html``}
    </section>
  `;
}

/** @internal pour tests : reset le formulaire. */
export function _resetCpBagFormForTests(): void {
  formState.item = '';
  formState.icon = '';
  formState.category = 'essentiel';
  formState.location = '';
}
